import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  listBrokerageAccounts,
  createBrokerageAccount,
  updateBrokerageAccount,
  deleteBrokerageAccount,
  setDefaultBrokerageAccount,
  getActiveInvestmentsCount,
  validateCredentials,
  type BrokerageAccount,
  type BrokerageAccountType,
  type AccountLimits,
} from "../services/brokerageService";
import { getSubscriptionStatus, syncSubscription, type SubscriptionCheckResult } from "../services/subscriptionService";
import {
  getAllMirrorHistory,
  getMirrorStatus,
  getBrokerDisplayName,
  getMirrorQueueStatusDisplay,
  type MirrorQueueEntry,
  type MirrorQueueStatus,
  type MirrorStatusSummary,
  type BrokerType,
} from "../services/brokerMirrorService";

export function BrokerageAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<BrokerageAccount[]>([]);
  const [accountLimits, setAccountLimits] = useState<AccountLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<BrokerageAccount | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeInvestmentCount, setActiveInvestmentCount] = useState<number>(0);

  // Add account form
  const [formAccountName, setFormAccountName] = useState("");
  const [formAccountType, setFormAccountType] = useState<BrokerageAccountType>("paper");
  const [formApiKey, setFormApiKey] = useState("");
  const [formApiSecret, setFormApiSecret] = useState("");
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formFractionalEnabled, setFormFractionalEnabled] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    portfolio_value?: string;
    buying_power?: string;
  } | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showGuide, setShowGuide] = useState(true);

  // Edit account modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAccount, setEditAccount] = useState<BrokerageAccount | null>(null);
  const [editAccountName, setEditAccountName] = useState("");
  const [editApiKey, setEditApiKey] = useState("");
  const [editApiSecret, setEditApiSecret] = useState("");
  const [editFractionalEnabled, setEditFractionalEnabled] = useState(true);
  const [editError, setEditError] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editValidationResult, setEditValidationResult] = useState<{
    valid: boolean;
    portfolio_value?: string;
    buying_power?: string;
  } | null>(null);

  // Subscription gating
  const [subscriptionCheck, setSubscriptionCheck] = useState<SubscriptionCheckResult | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<"accounts" | "mirror">("accounts");

  // Mirror Activity tab state
  const [mirrorStatus, setMirrorStatus] = useState<MirrorStatusSummary | null>(null);
  const [mirrorEntries, setMirrorEntries] = useState<MirrorQueueEntry[]>([]);
  const [mirrorTotal, setMirrorTotal] = useState(0);
  const [mirrorPages, setMirrorPages] = useState(0);
  const [mirrorPage, setMirrorPage] = useState(1);
  const [mirrorStatusFilter, setMirrorStatusFilter] = useState<MirrorQueueStatus | "">("");
  const [mirrorSymbolFilter, setMirrorSymbolFilter] = useState("");
  const [mirrorSideFilter, setMirrorSideFilter] = useState<"" | "buy" | "sell">("");
  const [mirrorBrokerFilter, setMirrorBrokerFilter] = useState<BrokerType | "">("");
  const [mirrorLoading, setMirrorLoading] = useState(false);
  const [mirrorError, setMirrorError] = useState<string | null>(null);
  const mirrorLimit = 500;
  const [mirrorSortField, setMirrorSortField] = useState<string>("created_at");
  const [mirrorSortDir, setMirrorSortDir] = useState<"asc" | "desc">("desc");

  const sortedMirrorEntries = useMemo(() => {
    const sorted = [...mirrorEntries];
    sorted.sort((a, b) => {
      let aVal: string | number = "";
      let bVal: string | number = "";
      switch (mirrorSortField) {
        case "symbol": aVal = a.symbol; bVal = b.symbol; break;
        case "side": aVal = a.side; bVal = b.side; break;
        case "qty": aVal = parseFloat(a.qty) || 0; bVal = parseFloat(b.qty) || 0; break;
        case "broker_fill_qty": aVal = parseFloat(a.broker_fill_qty || "0"); bVal = parseFloat(b.broker_fill_qty || "0"); break;
        case "alpaca_fill_price": aVal = parseFloat(a.alpaca_fill_price) || 0; bVal = parseFloat(b.alpaca_fill_price) || 0; break;
        case "broker_fill_price": aVal = parseFloat(a.broker_fill_price || "0"); bVal = parseFloat(b.broker_fill_price || "0"); break;
        case "broker_type": aVal = a.broker_type; bVal = b.broker_type; break;
        case "status": aVal = a.status; bVal = b.status; break;
        case "created_at": aVal = a.completed_at || a.created_at; bVal = b.completed_at || b.created_at; break;
        case "attempts": aVal = a.attempts; bVal = b.attempts; break;
      }
      if (aVal < bVal) return mirrorSortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return mirrorSortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [mirrorEntries, mirrorSortField, mirrorSortDir]);

  function handleMirrorSort(field: string) {
    if (mirrorSortField === field) {
      setMirrorSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setMirrorSortField(field);
      setMirrorSortDir("asc");
    }
  }

  function sortIndicator(field: string) {
    if (mirrorSortField !== field) return " ↕";
    return mirrorSortDir === "asc" ? " ↑" : " ↓";
  }

  useEffect(() => {
    loadAccounts();
    loadSubscriptionStatus();
  }, []);

  async function loadSubscriptionStatus() {
    try {
      // Sync subscription state from Stripe first
      try {
        await syncSubscription();
      } catch {
        // Sync is best-effort
      }
      const result = await getSubscriptionStatus();
      setSubscriptionCheck(result);
    } catch (err) {
      console.warn("[BrokerageAccountsPage] Failed to check subscription status:", err);
      // If we can't check, let them try and the backend will gate with 402
    }
  }

  async function loadAccounts() {
    try {
      setLoading(true);
      const response = await listBrokerageAccounts();
      setAccounts(response.accounts || []);
      setAccountLimits(response.account_limits || null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }

  // Mirror Activity data loading
  const loadMirrorData = useCallback(async () => {
    try {
      setMirrorLoading(true);
      setMirrorError(null);
      const [status, history] = await Promise.all([
        getMirrorStatus(),
        getAllMirrorHistory({
          page: mirrorPage,
          limit: mirrorLimit,
          status: mirrorStatusFilter || undefined,
          symbol: mirrorSymbolFilter || undefined,
          side: mirrorSideFilter || undefined,
          broker_type: mirrorBrokerFilter || undefined,
        }),
      ]);
      setMirrorStatus(status);
      setMirrorEntries(history.entries || []);
      setMirrorTotal(history.total);
      setMirrorPages(history.pages);
    } catch (err) {
      setMirrorError(err instanceof Error ? err.message : "Failed to load mirror data");
    } finally {
      setMirrorLoading(false);
    }
  }, [mirrorPage, mirrorStatusFilter, mirrorSymbolFilter, mirrorSideFilter, mirrorBrokerFilter]);

  // Load mirror data when tab is active and filters change
  useEffect(() => {
    if (activeTab === "mirror") {
      loadMirrorData();
    }
  }, [activeTab, loadMirrorData]);

  // Reset page when mirror filters change
  useEffect(() => {
    setMirrorPage(1);
  }, [mirrorStatusFilter, mirrorSymbolFilter, mirrorSideFilter, mirrorBrokerFilter]);

  async function handleValidateCredentials() {
    if (!formApiKey || !formApiSecret) {
      setFormError("API Key and Secret are required");
      return;
    }

    try {
      setFormLoading(true);
      setFormError(null);
      setValidationResult(null);

      const result = await validateCredentials({
        api_key: formApiKey,
        api_secret: formApiSecret,
        account_type: formAccountType,
      });

      if (result.valid) {
        setValidationResult({
          valid: true,
          portfolio_value: result.portfolio_value,
          buying_power: result.buying_power,
        });
      } else {
        setFormError(result.error || "Invalid credentials");
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Validation failed");
    } finally {
      setFormLoading(false);
    }
  }

  async function handleAddAccount(e: React.FormEvent) {
    e.preventDefault();
    
    if (!formAccountName || !formApiKey || !formApiSecret) {
      setFormError("All fields are required");
      return;
    }

    try {
      setFormLoading(true);
      setFormError(null);

      await createBrokerageAccount({
        account_name: formAccountName,
        account_type: formAccountType,
        api_key: formApiKey,
        api_secret: formApiSecret,
        is_default: formIsDefault,
        fractional_enabled: formFractionalEnabled,
      });

      // Reset form and reload
      setShowAddModal(false);
      resetForm();
      await loadAccounts();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create account";
      if (msg.includes("subscription") || msg.includes("402")) {
        setFormError("An active subscription is required. Please visit the Subscription page to subscribe.");
        // Re-check subscription status to update the banner
        loadSubscriptionStatus();
      } else {
        setFormError(msg);
      }
    } finally {
      setFormLoading(false);
    }
  }

  function resetForm() {
    setFormAccountName("");
    setFormAccountType("paper");
    setFormApiKey("");
    setFormApiSecret("");
    setFormIsDefault(false);
    setFormFractionalEnabled(true);
    setFormError(null);
    setValidationResult(null);
    setAgreedToTerms(false);
  }

  async function handleDeleteClick(account: BrokerageAccount) {
    setAccountToDelete(account);
    setDeleteError(null);
    setActiveInvestmentCount(0);
    setDeleteLoading(true);
    setShowDeleteModal(true);

    try {
      // Check for active investments
      const result = await getActiveInvestmentsCount(account.id);
      setActiveInvestmentCount(result.count);
    } catch (err) {
      console.error("Failed to check active investments:", err);
      // If we can't check, let them try to delete and let backend reject if needed
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleConfirmDelete() {
    if (!accountToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteBrokerageAccount(accountToDelete.id);
      setShowDeleteModal(false);
      setAccountToDelete(null);
      await loadAccounts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete account";
      if (errorMessage.includes("active investments")) {
        setDeleteError("This account has active strategies. Please close all strategies and liquidate positions before deleting.");
      } else {
        setDeleteError(errorMessage);
      }
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleSetDefault(account: BrokerageAccount) {
    try {
      await setDefaultBrokerageAccount(account.id);
      await loadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set default");
    }
  }

  function handleEditClick(account: BrokerageAccount) {
    setEditAccount(account);
    setEditAccountName(account.account_name);
    setEditFractionalEnabled(account.fractional_enabled !== false);
    setEditApiKey("");
    setEditApiSecret("");
    setEditError(null);
    setEditValidationResult(null);
    setShowEditModal(true);
  }

  async function handleEditValidateCredentials() {
    if (!editApiKey || !editApiSecret || !editAccount) {
      setEditError("API Key and Secret are required to validate");
      return;
    }

    try {
      setEditLoading(true);
      setEditError(null);
      setEditValidationResult(null);

      const result = await validateCredentials({
        api_key: editApiKey,
        api_secret: editApiSecret,
        account_type: editAccount.account_type,
      });

      if (result.valid) {
        setEditValidationResult({
          valid: true,
          portfolio_value: result.portfolio_value,
          buying_power: result.buying_power,
        });
      } else {
        setEditError(result.error || "Invalid credentials");
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Validation failed");
    } finally {
      setEditLoading(false);
    }
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editAccount) return;

    if (!editAccountName.trim()) {
      setEditError("Account name is required");
      return;
    }

    // Build update payload — only include changed fields
    const update: { account_name?: string; api_key?: string; api_secret?: string; fractional_enabled?: boolean } = {};

    if (editAccountName !== editAccount.account_name) {
      update.account_name = editAccountName;
    }

    // Always include fractional_enabled if it changed
    const currentFractional = editAccount.fractional_enabled !== false;
    if (editFractionalEnabled !== currentFractional) {
      update.fractional_enabled = editFractionalEnabled;
    }

    if (editApiKey && editApiSecret) {
      if (!editValidationResult?.valid) {
        setEditError("Please validate your new credentials before saving");
        return;
      }
      update.api_key = editApiKey;
      update.api_secret = editApiSecret;
    } else if (editApiKey || editApiSecret) {
      setEditError("Both API Key and API Secret are required to update credentials");
      return;
    }

    if (Object.keys(update).length === 0) {
      setShowEditModal(false);
      return;
    }

    try {
      setEditLoading(true);
      setEditError(null);
      await updateBrokerageAccount(editAccount.id, update);
      setShowEditModal(false);
      await loadAccounts();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to update account");
    } finally {
      setEditLoading(false);
    }
  }

  function formatCurrency(value: string | undefined): string {
    if (!value) return "$0.00";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }

  function formatMirrorDateTime(dateStr: string | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case "active":
        return "bg-accent-500/20 text-accent-400";
      case "pending":
        return "bg-yellow-500/20 text-yellow-400";
      case "error":
        return "bg-red-500/20 text-red-400";
      case "suspended":
        return "bg-surface-600 text-surface-300";
      default:
        return "bg-surface-600 text-surface-300";
    }
  }

  function getAccountTypeColor(type: BrokerageAccountType): string {
    return type === "paper"
      ? "bg-blue-500/20 text-blue-400"
      : "bg-purple-500/20 text-purple-400";
  }

  // Subscription gating: disable add if no active subscription (unless admin)
  const subscriptionBlocked =
    subscriptionCheck !== null && !subscriptionCheck.has_access && !subscriptionCheck.is_admin;

  return (
    <div>
      <PageHeader
        title="Brokerage Accounts"
        subtitle="Connect and manage your Alpaca trading accounts"
        backgroundImage="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {error && (
          <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="glass-card p-1 inline-flex rounded-xl mb-6">
          <button
            onClick={() => setActiveTab("accounts")}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === "accounts"
                ? "bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/10"
                : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Accounts
              {accounts.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-surface-700 text-xs">{accounts.length}</span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("mirror")}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === "mirror"
                ? "bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/10"
                : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Mirror Activity
              {mirrorStatus && mirrorStatus.today_enqueued > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-surface-700 text-xs">{mirrorStatus.today_enqueued}</span>
              )}
            </span>
          </button>
        </div>

        {activeTab === "accounts" && (<>
        {/* Subscription Required Banner */}
        {subscriptionCheck && !subscriptionCheck.has_access && !subscriptionCheck.is_admin && (
          <div className="mb-6 glass-card bg-yellow-500/10 border-yellow-500/30 px-4 py-4">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-300">Active subscription required</p>
                <p className="text-sm text-surface-400 mt-1">
                  You need an active subscription to connect brokerage accounts.{" "}
                  {subscriptionCheck.in_grace_period && subscriptionCheck.grace_period_ends_at
                    ? `Your grace period ends ${new Date(subscriptionCheck.grace_period_ends_at).toLocaleDateString()}.`
                    : subscriptionCheck.reason}
                </p>
                <Link
                  to="/subscription"
                  className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
                >
                  View plans & subscribe
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Account Limits Info */}
        {accountLimits && !accountLimits.is_admin && (
          <div className="mb-4 glass-card bg-surface-800/50 border-surface-700/50 px-4 py-3">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-primary-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="text-surface-300">
                  Your account is limited to <strong className="text-surface-100">1 paper</strong> and <strong className="text-surface-100">1 live</strong> trading account.
                  {" "}({accountLimits.paper_count}/1 paper, {accountLimits.live_count}/1 live used)
                </p>
                {(!accountLimits.can_add_paper && !accountLimits.can_add_live) && (
                  <p className="text-surface-400 mt-1">
                    You've reached your account limit. Sign in as a different user or contact an admin for additional accounts.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Getting Started Guide */}
        <div className="mb-6">
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors mb-3"
          >
            <svg className={`h-4 w-4 transition-transform ${showGuide ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showGuide ? 'Hide' : 'Show'} Getting Started Guide
          </button>

          {showGuide && (
            <div className="glass-card p-6 border border-surface-700/50">
              <h3 className="text-lg font-semibold text-surface-100 mb-2 flex items-center gap-2">
                <svg className="h-5 w-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Try Your Strategies with Alpaca Paper Trading
              </h3>
              <p className="text-sm text-surface-400 mb-5">
                If you already have an Alpaca account, you can connect it here to test your strategies using paper trading — simulated trades with no real money at risk.
              </p>

              {/* Step-by-step instructions */}
              <div className="space-y-4 mb-6">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-semibold">1</div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">Go to Alpaca and log in</p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      Visit{" "}
                      <a href="https://alpaca.markets/" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors underline">alpaca.markets</a>
                      {" "}and click <strong className="text-surface-300">Login → Trading API</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-semibold">2</div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">Navigate to the API Keys section</p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      Once logged in at{" "}
                      <a href="https://app.alpaca.markets/dashboard/overview" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors underline">app.alpaca.markets/dashboard/overview</a>,
                      locate the <strong className="text-surface-300">API Keys</strong> panel.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-semibold">3</div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">Generate a new API Key &amp; Secret</p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      Click <strong className="text-surface-300">Generate</strong> (or <strong className="text-surface-300">Regenerate</strong>) to create a new key pair. You will see your <em>Endpoint</em>, <em>Key</em>, and <em>Secret</em>.
                    </p>
                    {/* Visual illustration of the Alpaca API Keys panel */}
                    <div className="mt-3 rounded-lg border border-surface-600/50 bg-surface-900/60 p-4 max-w-sm">
                      <p className="text-xs font-semibold text-surface-300 mb-3">API Keys</p>
                      <div className="space-y-2.5">
                        <div>
                          <p className="text-[11px] text-surface-400 mb-0.5">Endpoint</p>
                          <div className="bg-surface-800 border border-surface-700 rounded px-2.5 py-1.5">
                            <span className="text-xs text-surface-300 font-mono">https://paper-api.alpaca.markets/v2</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] text-surface-400 mb-0.5">Key</p>
                          <div className="bg-surface-800 border border-surface-700 rounded px-2.5 py-1.5">
                            <span className="text-xs text-surface-500 font-mono">••••••••••••••••••••</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] text-surface-400 mb-0.5">Secret</p>
                          <div className="bg-surface-800 border border-surface-700 rounded px-2.5 py-1.5">
                            <span className="text-xs text-surface-500 font-mono">••••••••••••••••••••</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-yellow-400/80 mt-2 italic">Your secret key will disappear after refreshing or navigating away.</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-sm font-semibold">4</div>
                  <div>
                    <p className="text-sm font-medium text-surface-200">Paste your Key &amp; Secret here</p>
                    <p className="text-xs text-surface-400 mt-0.5">
                      Click <strong className="text-surface-300">"Connect Alpaca Account"</strong> above, enter your API Key and Secret, validate, and connect.
                    </p>
                  </div>
                </div>
              </div>

              {/* Disclaimers section */}
              <div className="border-t border-surface-700/50 pt-4 space-y-3">
                <p className="text-xs text-surface-400">
                  <strong className="text-yellow-400">You are solely responsible for keeping your API Key and Secret secure on your device. Do not share them with anyone.</strong>{" "}
                  TradeCraft is not responsible for any unauthorized access, misuse, or loss resulting from the exposure of your API credentials to any party outside of this platform.
                </p>

                <p className="text-xs text-surface-400">
                  <strong className="text-surface-300">Important:</strong> Past performance of any strategy — whether backtested or paper-traded — is not a guarantee of future results. All trading involves risk, including the possible loss of principal.
                </p>

                <p className="text-xs text-surface-400">
                  TradeCraft is not a brokerage, broker-dealer, or registered investment advisor. We do not provide financial, investment, or trading advice. This platform is provided solely as a tool to backtest and evaluate trading strategies. Any decision to execute trades is made entirely at your own discretion and risk.
                </p>

                <p className="text-xs text-surface-400">
                  Please review Alpaca's{" "}
                  <a href="https://alpaca.markets/disclosures" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors underline">Terms, Disclosures &amp; Privacy Policy</a>
                  {" "}to understand how your brokerage account and data are managed.
                </p>

                <p className="text-xs text-surface-300">
                  By connecting a brokerage account, you acknowledge and agree to TradeCraft's{" "}
                  <Link to="/terms" className="text-primary-400 hover:text-primary-300 transition-colors underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-primary-400 hover:text-primary-300 transition-colors underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Add Account Button */}
        <div className="mb-6 flex justify-between items-center">
          <p className="text-surface-400">
            {accounts.length === 0
              ? "No brokerage accounts connected yet."
              : `${accounts.length} account${accounts.length !== 1 ? "s" : ""} connected`}
          </p>
          <button
            onClick={() => {
              // Auto-select available account type
              if (accountLimits && !accountLimits.can_add_paper && accountLimits.can_add_live) {
                setFormAccountType("live");
              } else {
                setFormAccountType("paper");
              }
              setShowAddModal(true);
            }}
            disabled={subscriptionBlocked || (accountLimits ? (!accountLimits.can_add_paper && !accountLimits.can_add_live) : false)}
            className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              subscriptionBlocked
                ? "An active subscription is required to connect brokerage accounts."
                : accountLimits && !accountLimits.can_add_paper && !accountLimits.can_add_live
                ? "Account limit reached. Sign in as a different user or contact an admin."
                : undefined
            }
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Connect Alpaca Account
          </button>
        </div>

        {/* Accounts List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading accounts...</p>
            </div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="glass-card p-6 sm:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-surface-100 mb-2">No accounts connected</h3>
            <p className="text-surface-400 mb-6">
              Connect your Alpaca account to start automated trading.
            </p>
            <button
              onClick={() => {
                if (accountLimits && !accountLimits.can_add_paper && accountLimits.can_add_live) {
                  setFormAccountType("live");
                } else {
                  setFormAccountType("paper");
                }
                setShowAddModal(true);
              }}
              disabled={subscriptionBlocked || (accountLimits ? (!accountLimits.can_add_paper && !accountLimits.can_add_live) : false)}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Connect Alpaca Account
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="glass-card overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-surface-100">
                      {account.account_name}
                    </h3>
                    {account.is_default && (
                      <span className="px-2 py-1 text-xs font-medium bg-primary-500/20 text-primary-400 rounded">
                        Default
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getAccountTypeColor(account.account_type)}`}>
                      {account.account_type === "paper" ? "Paper" : "Live"}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(account.status)}`}>
                      {account.status}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${account.fractional_enabled !== false ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                      {account.fractional_enabled !== false ? "Fractional" : "Whole Shares"}
                    </span>
                  </div>

                  {account.alpaca_account_id && (
                    <p className="text-sm text-surface-400 mb-2">
                      Account ID: {account.alpaca_account_id.substring(0, 8)}...
                    </p>
                  )}

                  {/* Financial Summary */}
                  {account.portfolio_value && (
                    <div className="grid grid-cols-3 gap-2 mb-3 py-2 border-t border-b border-surface-700/50">
                      <div>
                        <p className="text-xs text-surface-500">Portfolio</p>
                        <p className="text-sm font-medium text-surface-200">{formatCurrency(account.portfolio_value)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-surface-500">Cash</p>
                        <p className="text-sm font-medium text-surface-200">{formatCurrency(account.cash_balance)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-surface-500">Buying Power</p>
                        <p className="text-sm font-medium text-purple-400">{formatCurrency(account.buying_power)}</p>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-surface-500">
                    Connected {new Date(account.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="bg-surface-800/50 px-5 py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 border-t border-surface-700/50">
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/brokerage/${account.id}/positions`)}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Positions
                    </button>
                    <button
                      onClick={() => navigate(`/brokerage/${account.id}/orders`)}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Order History
                    </button>
                    <button
                      onClick={() => navigate(`/brokerage/${account.id}/mirror`)}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Mirror
                    </button>
                  </div>
                  <div className="flex gap-3">
                    {!account.is_default && (
                      <button
                        onClick={() => handleSetDefault(account)}
                        className="text-sm text-surface-400 hover:text-surface-200 transition-colors"
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(account)}
                      className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClick(account)}
                      className="text-sm text-red-400 hover:text-red-300 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}

      {/* Mirror Activity Tab */}
      {activeTab === "mirror" && (
        <div>
          {mirrorError && (
            <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3 text-sm">
              {mirrorError}
            </div>
          )}

          {/* Summary cards */}
          {mirrorStatus && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Active Links</p>
                <p className="text-xl font-semibold text-surface-100">{mirrorStatus.active_links}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Total Links</p>
                <p className="text-xl font-semibold text-surface-300">{mirrorStatus.total_links}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Today Enqueued</p>
                <p className="text-xl font-semibold text-surface-100">{mirrorStatus.today_enqueued}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Completed</p>
                <p className="text-xl font-semibold text-accent-400">{mirrorStatus.today_completed}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Failed</p>
                <p className="text-xl font-semibold text-red-400">{mirrorStatus.today_failed}</p>
              </div>
              <div className="glass-card p-4">
                <p className="text-xs text-surface-500 mb-1">Pending</p>
                <p className="text-xl font-semibold text-yellow-400">{mirrorStatus.today_pending}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 sm:max-w-xs">
              <input
                type="text"
                value={mirrorSymbolFilter}
                onChange={(e) => setMirrorSymbolFilter(e.target.value.toUpperCase())}
                placeholder="Filter by symbol..."
                className="w-full px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
              />
            </div>
            <select
              value={mirrorStatusFilter}
              onChange={(e) => setMirrorStatusFilter(e.target.value as MirrorQueueStatus | "")}
              className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
            <select
              value={mirrorSideFilter}
              onChange={(e) => setMirrorSideFilter(e.target.value as "" | "buy" | "sell")}
              className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
            >
              <option value="">All sides</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
            <select
              value={mirrorBrokerFilter}
              onChange={(e) => setMirrorBrokerFilter(e.target.value as BrokerType | "")}
              className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
            >
              <option value="">All brokers</option>
              <option value="schwab">Charles Schwab</option>
              <option value="ibkr">Interactive Brokers</option>
              <option value="tastytrade">Tastytrade</option>
            </select>
            <button
              onClick={loadMirrorData}
              disabled={mirrorLoading}
              className="btn-secondary text-sm"
            >
              {mirrorLoading ? "Loading..." : "Refresh"}
            </button>
          </div>

          {/* Mirror history */}
          {mirrorLoading && mirrorEntries.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-4">
                <div className="spinner w-8 h-8" />
                <p className="text-surface-400">Loading mirror activity...</p>
              </div>
            </div>
          ) : mirrorEntries.length === 0 ? (
            <div className="glass-card p-6 sm:p-12 text-center">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-surface-100 mb-2">No mirror activity{mirrorStatusFilter || mirrorSymbolFilter || mirrorBrokerFilter ? " matching filters" : " yet"}</h3>
              <p className="text-surface-400 mb-6 max-w-md mx-auto">
                {mirrorStatus && mirrorStatus.total_links === 0
                  ? "Link an external broker from an account's Mirror page to start mirroring trades."
                  : "When TradeCraft fills orders on Alpaca, mirrored trades will appear here."}
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-surface-400 mb-2">{mirrorTotal} entries</p>
              {/* Desktop table */}
              <div className="hidden md:block glass-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-700/50">
                        <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("symbol")}>Symbol{sortIndicator("symbol")}</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("side")}>Side{sortIndicator("side")}</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("qty")}>Qty{sortIndicator("qty")}</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("broker_fill_qty")}>Broker Qty{sortIndicator("broker_fill_qty")}</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("alpaca_fill_price")}>Alpaca Price{sortIndicator("alpaca_fill_price")}</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("broker_fill_price")}>Broker Price{sortIndicator("broker_fill_price")}</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("broker_type")}>Broker{sortIndicator("broker_type")}</th>
                        <th className="text-center px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("status")}>Status{sortIndicator("status")}</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("created_at")}>Time{sortIndicator("created_at")}</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-surface-400 uppercase tracking-wider cursor-pointer hover:text-surface-200 select-none" onClick={() => handleMirrorSort("attempts")}>Attempts{sortIndicator("attempts")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-700/30">
                      {sortedMirrorEntries.map((entry) => {
                        const statusDisplay = getMirrorQueueStatusDisplay(entry.status);
                        return (
                          <React.Fragment key={entry.id}>
                            <tr className="hover:bg-surface-800/30 transition-colors">
                              <td className="px-4 py-3 font-medium text-surface-100">{entry.symbol}</td>
                              <td className="px-4 py-3">
                                <span className={entry.side === "buy" ? "text-accent-400" : "text-red-400"}>
                                  {entry.side.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-surface-200">{entry.qty}</td>
                              <td className="px-4 py-3 text-right text-surface-200">{entry.broker_fill_qty ? Math.floor(parseFloat(entry.broker_fill_qty)) : "—"}</td>
                              <td className="px-4 py-3 text-right text-surface-200">{formatCurrency(entry.alpaca_fill_price)}</td>
                              <td className="px-4 py-3 text-right text-surface-200">{entry.broker_fill_price ? formatCurrency(entry.broker_fill_price) : "—"}</td>
                              <td className="px-4 py-3 text-surface-300">{getBrokerDisplayName(entry.broker_type)}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusDisplay.colorClass}`}>
                                  {statusDisplay.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-surface-400">{formatMirrorDateTime(entry.completed_at || entry.created_at)}</td>
                              <td className="px-4 py-3 text-right text-surface-400">{entry.attempts}/{entry.max_attempts}</td>
                            </tr>
                            {entry.error_message && (entry.status === "failed" || entry.status === "skipped") && (
                              <tr className={entry.status === "skipped" ? "bg-yellow-900/10" : "bg-red-900/10"}>
                                <td colSpan={10} className={`px-4 py-1.5 text-xs ${entry.status === "skipped" ? "text-yellow-400" : "text-red-400"}`}>
                                  <span className={`font-medium ${entry.status === "skipped" ? "text-yellow-500/70" : "text-red-500/70"}`}>{entry.status === "skipped" ? "Skipped:" : "Error:"}</span> {entry.error_message}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile card list */}
              <div className="md:hidden space-y-3">
                {mirrorEntries.map((entry) => {
                  const statusDisplay = getMirrorQueueStatusDisplay(entry.status);
                  return (
                    <div key={entry.id} className="glass-card p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-surface-100">{entry.symbol}</span>
                          <span className={entry.side === "buy" ? "text-accent-400 text-xs font-medium" : "text-red-400 text-xs font-medium"}>
                            {entry.side.toUpperCase()}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusDisplay.colorClass}`}>
                          {statusDisplay.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-surface-500">Qty:</span>{" "}
                          <span className="text-surface-200">{entry.qty}</span>
                        </div>
                        <div>
                          <span className="text-surface-500">Broker:</span>{" "}
                          <span className="text-surface-200">{getBrokerDisplayName(entry.broker_type)}</span>
                        </div>
                        <div>
                          <span className="text-surface-500">Alpaca:</span>{" "}
                          <span className="text-surface-200">{formatCurrency(entry.alpaca_fill_price)}</span>
                        </div>
                        <div>
                          <span className="text-surface-500">Broker Price:</span>{" "}
                          <span className="text-surface-200">{entry.broker_fill_price ? formatCurrency(entry.broker_fill_price) : "—"}</span>
                        </div>
                      </div>
                      {entry.error_message && (
                        <p className="mt-2 text-xs text-red-400 truncate" title={entry.error_message}>
                          {entry.error_message}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-surface-500">
                        {formatMirrorDateTime(entry.completed_at || entry.created_at)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {mirrorPages > 1 && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <p className="text-sm text-surface-400">
                    Showing {(mirrorPage - 1) * mirrorLimit + 1}–{Math.min(mirrorPage * mirrorLimit, mirrorTotal)} of {mirrorTotal}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMirrorPage((p) => Math.max(1, p - 1))}
                      disabled={mirrorPage <= 1}
                      className="px-3 py-1.5 text-sm bg-surface-800 border border-surface-600 rounded-lg text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm text-surface-400">
                      {mirrorPage} / {mirrorPages}
                    </span>
                    <button
                      onClick={() => setMirrorPage((p) => Math.min(mirrorPages, p + 1))}
                      disabled={mirrorPage >= mirrorPages}
                      className="px-3 py-1.5 text-sm bg-surface-800 border border-surface-600 rounded-lg text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowAddModal(false)} />
            
            <div className="relative glass-card max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-surface-100">
                  Connect Alpaca Account
                </h3>
                <button
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddAccount}>
                {formError && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                {validationResult?.valid && (
                  <div className="mb-4 bg-accent-500/10 border border-accent-500/30 text-accent-400 px-3 py-2 rounded-lg text-sm">
                    ✓ Credentials valid! Portfolio: {formatCurrency(validationResult.portfolio_value)}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Account Name
                    </label>
                    <input
                      type="text"
                      value={formAccountName}
                      onChange={(e) => setFormAccountName(e.target.value)}
                      placeholder="My Trading Account"
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Account Type
                    </label>
                    <select
                      value={formAccountType}
                      onChange={(e) => {
                        setFormAccountType(e.target.value as BrokerageAccountType);
                        setValidationResult(null);
                      }}
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    >
                      <option value="paper" disabled={accountLimits ? !accountLimits.can_add_paper : false}>
                        Paper Trading (Recommended for testing){accountLimits && !accountLimits.can_add_paper ? " — Limit reached" : ""}
                      </option>
                      <option value="live" disabled={accountLimits ? !accountLimits.can_add_live : false}>
                        Live Trading (Real money){accountLimits && !accountLimits.can_add_live ? " — Limit reached" : ""}
                      </option>
                    </select>
                    {accountLimits && !accountLimits.is_admin && (
                      <p className="mt-1 text-xs text-surface-500">
                        Limit: 1 paper + 1 live account per user.{" "}
                        {(!accountLimits.can_add_paper || !accountLimits.can_add_live) && (
                          <span className="text-yellow-400">Sign in as a different user for additional accounts.</span>
                        )}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      API Key
                    </label>
                    <input
                      type="text"
                      value={formApiKey}
                      onChange={(e) => { setFormApiKey(e.target.value); setValidationResult(null); }}
                      placeholder="PK..."
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      API Secret
                    </label>
                    <input
                      type="password"
                      value={formApiSecret}
                      onChange={(e) => { setFormApiSecret(e.target.value); setValidationResult(null); }}
                      placeholder="••••••••••••••••"
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={formIsDefault}
                      onChange={(e) => setFormIsDefault(e.target.checked)}
                      className="h-4 w-4 text-primary-500 bg-surface-800 border-surface-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="isDefault" className="ml-2 text-sm text-surface-300">
                      Set as default account
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="fractionalEnabled"
                      checked={formFractionalEnabled}
                      onChange={(e) => setFormFractionalEnabled(e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-primary-500 bg-surface-800 border-surface-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="fractionalEnabled" className="ml-2 text-sm text-surface-300">
                      Allow fractional shares
                      <span className="block text-xs text-surface-500 mt-0.5">
                        Uncheck if you plan to mirror trades to Charles Schwab or another brokerage that doesn't support fractional shares. Orders will be rounded down to whole shares.
                      </span>
                    </label>
                  </div>

                  {/* Terms agreement checkbox */}
                  <div className="flex items-start mt-2 pt-3 border-t border-surface-700/30">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-primary-500 bg-surface-800 border-surface-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="agreeTerms" className="ml-2 text-xs text-surface-400 leading-relaxed">
                      By connecting my brokerage account, I agree to TradeCraft's{" "}
                      <Link to="/terms" target="_blank" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>
                      {" "}and{" "}
                      <Link to="/privacy" target="_blank" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link>.
                      {" "}I understand that past performance is not a guarantee of future results, and that TradeCraft is not a brokerage or financial advisor.
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-between">
                  <button
                    type="button"
                    onClick={handleValidateCredentials}
                    disabled={formLoading || !formApiKey || !formApiSecret}
                    className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formLoading ? "Validating..." : "Validate Credentials"}
                  </button>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowAddModal(false); resetForm(); }}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading || !agreedToTerms}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!agreedToTerms ? "You must agree to the Terms of Service and Privacy Policy" : undefined}
                    >
                      {formLoading ? "Connecting..." : "Connect Account"}
                    </button>
                  </div>
                </div>
              </form>

              <div className="mt-6 pt-4 border-t border-surface-700/50 space-y-2">
                <p className="text-xs text-surface-400">
                  <strong className="text-surface-300">How to get API keys:</strong> Log in to your Alpaca account at{" "}
                  <a
                    href="https://app.alpaca.markets"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    app.alpaca.markets
                  </a>
                  , go to API Keys, and generate a new key pair. See the Getting Started guide on this page for detailed steps.
                </p>
                <p className="text-[11px] text-surface-500">
                  <strong className="text-yellow-400/80">Keep your API Key and Secret secure.</strong> Do not share them with anyone. TradeCraft is not responsible for any misuse.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && editAccount && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowEditModal(false)} />

            <div className="relative glass-card max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-surface-100">
                  Edit Account
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-surface-400 hover:text-surface-200 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                {editError && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg text-sm">
                    {editError}
                  </div>
                )}

                {editValidationResult?.valid && (
                  <div className="mb-4 bg-accent-500/10 border border-accent-500/30 text-accent-400 px-3 py-2 rounded-lg text-sm">
                    ✓ Credentials valid! Portfolio: {formatCurrency(editValidationResult.portfolio_value)}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Account Name
                    </label>
                    <input
                      type="text"
                      value={editAccountName}
                      onChange={(e) => setEditAccountName(e.target.value)}
                      className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      required
                    />
                  </div>

                  <div className="border-t border-surface-700/50 pt-4">
                    <p className="text-sm font-medium text-surface-300 mb-1">
                      Update API Credentials <span className="text-surface-500 font-normal">(optional)</span>
                    </p>
                    <p className="text-xs text-surface-500 mb-3">
                      Leave blank to keep your current credentials. Provide both key and secret to update.
                    </p>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          New API Key
                        </label>
                        <input
                          type="text"
                          value={editApiKey}
                          onChange={(e) => { setEditApiKey(e.target.value); setEditValidationResult(null); }}
                          placeholder="Leave blank to keep current"
                          className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          New API Secret
                        </label>
                        <input
                          type="password"
                          value={editApiSecret}
                          onChange={(e) => { setEditApiSecret(e.target.value); setEditValidationResult(null); }}
                          placeholder="Leave blank to keep current"
                          className="w-full px-3 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 placeholder-surface-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-surface-700/50">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="editFractionalEnabled"
                      checked={editFractionalEnabled}
                      onChange={(e) => setEditFractionalEnabled(e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-primary-500 bg-surface-800 border-surface-600 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="editFractionalEnabled" className="ml-2 text-sm text-surface-300">
                      Allow fractional shares
                      <span className="block text-xs text-surface-500 mt-0.5">
                        Uncheck if you plan to mirror trades to Charles Schwab or another brokerage that doesn't support fractional shares. Orders will be rounded down to whole shares.
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-between">
                  {editApiKey && editApiSecret ? (
                    <button
                      type="button"
                      onClick={handleEditValidateCredentials}
                      disabled={editLoading}
                      className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editLoading ? "Validating..." : "Validate Credentials"}
                    </button>
                  ) : (
                    <div />
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editLoading || (!!editApiKey && !!editApiSecret && !editValidationResult?.valid)}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      title={editApiKey && editApiSecret && !editValidationResult?.valid ? "Validate credentials before saving" : undefined}
                    >
                      {editLoading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </form>

              <div className="mt-6 pt-4 border-t border-surface-700/50 space-y-2">
                <p className="text-xs text-surface-400">
                  <strong className="text-surface-300">How to get API keys:</strong> Log in to your Alpaca account at{" "}
                  <a
                    href="https://app.alpaca.markets"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    app.alpaca.markets
                  </a>
                  , go to API Keys, and generate a new key pair.
                </p>
                <p className="text-[11px] text-surface-500">
                  <strong className="text-yellow-400/80">Keep your API Key and Secret secure.</strong> Do not share them with anyone. TradeCraft is not responsible for any misuse.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setAccountToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        title={activeInvestmentCount > 0 ? "Cannot Delete Account" : "Delete Brokerage Account"}
        message={
          deleteError 
            ? deleteError 
            : activeInvestmentCount > 0
              ? `This account has ${activeInvestmentCount} active strateg${activeInvestmentCount === 1 ? 'y' : 'ies'}. Please close all strategies and liquidate positions from the Portfolio page before deleting this account.`
              : `Are you sure you want to delete "${accountToDelete?.account_name}"? This action cannot be undone.`
        }
        confirmText={activeInvestmentCount > 0 ? "OK" : "Delete Account"}
        cancelText="Cancel"
        variant={activeInvestmentCount > 0 ? "warning" : "danger"}
        isLoading={deleteLoading}
        hideConfirm={activeInvestmentCount > 0}
      />
    </div>
  );
}
